/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import { BillingPlan, Site } from '@api/model';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Collapse,
  Grid,
  Link as MuiLink,
  TextField,
  Typography
} from '@mui/material';
import { FormikErrors } from 'formik/dist/types';
import { stubComponent, TagInputList } from 'ish-ui';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { SiteValues } from '../../models/Sites';
import { getSites } from '../../redux/actions/Sites';
import { useAppDispatch, useAppSelector } from '../../redux/hooks/redux';
import { renderSelectItems, renderSelectItemsWithEmpty } from '../../utils';
import AutosizeInput from '../common/AutosizeInput';
import { TemplateField } from '../common/TemplateChoser';
import WarningMessage from '../common/WarningMessage';

interface Props {
  classes: any;
  isNew: boolean;
  collegeKey: string;
  site: SiteValues;
  onClickDelete?: any;
  initial: Site;
  error: SiteValues;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<FormikErrors<SiteValues>> | Promise<void>;
  setFieldError: any;
  handleChange: any;
  billingPlan: BillingPlan;
}

const URLs = (
  {
    classes,
    isNew,
    site,
    error,
    setFieldValue,
    setFieldError,
    handleChange,
    billingPlan
  }: Props
) => {
  const tags = useAppSelector((state) => state.tags);

  const dispatch = useAppDispatch();

  const onKeyChange: any = (e) => {
    const { value } = e.target;
    setFieldValue('key', value, true);
  };

  const onAddDomain = async (params) => {
    await setFieldValue('domains', { ...site.domains, [params.inputProps.value]: '' });

    if (isCustomDomainsAllow() && !Object.keys(site.domains).includes(site?.primaryDomain)) {
      setFieldError('primaryDomain', 'Primary domain is required when domains specified');
      await setFieldValue('primaryDomain', null, false);
    }
  };

  const onPrimaryDomainChange: any = (params) => {
    let shouldValidte = true;
    let newPrimaryDomain = params.target.value;

    if (newPrimaryDomain) {
      // drop invalid primary domain
      if (newPrimaryDomain && !Object.keys(site.domains).includes(newPrimaryDomain)) {
        newPrimaryDomain = null;
      }
      // if primary domain is not setted or drop set it
      if (!site.domains.length && !newPrimaryDomain) {
        newPrimaryDomain = Object.keys(site.domains)[0];
      }
    } else {
      if (isCustomDomainsAllow()) {
        shouldValidte = false;
        setFieldError('primaryDomain', 'Primary domain is required when domains specified');
      }
    }
    setFieldValue('primaryDomain', newPrimaryDomain, shouldValidte);
  };

  const isCustomDomainsAllow = () => {
    return billingPlan === BillingPlan['premium-21'] || ![BillingPlan['starter-21'], BillingPlan['basic-21']].includes(billingPlan);
  };

  const domainItems = useMemo(() => {
    return isCustomDomainsAllow() ?
      renderSelectItems({ items: Object.keys(site.domains) }) :
      renderSelectItemsWithEmpty({ items: Object.keys(site.domains) });
  }, [site.domains]);

  const domainWarnings = useMemo(() => Object.keys(site.domains).map((d) => (site.domains[d]
    ? (
        <WarningMessage
          key={d}
          warning={(
            <span>
            <strong>
              {d}
              :
              {' '}
            </strong>
              {site.domains[d]}
          </span>
          )}
        />
    )
    : null)),
  [site.domains]);

  const onDomainsChange = (e, v: string[]) => {
    const updated = v.reduce((p, c) => ({ ...p, [c]: site.domains[c] || '' }), {});

    let shouldValidate = true;
    if (Object.keys(updated).length !== 0) {
      if (isCustomDomainsAllow() && !Object.keys(updated).includes(site?.primaryDomain)) {
        shouldValidate = false;
        setFieldError('primaryDomain', 'Primary domain is required when domains specified');
        setFieldValue('primaryDomain', null, shouldValidate);
      }
    } else {
      setFieldValue('primaryDomain', null, shouldValidate);
    }
    setFieldValue('domains', updated, shouldValidate);
  };

  const onRecheck = () => {
    dispatch(getSites());
  };

  return (
    <Grid container>
      <Grid item xs={6} className={classes.pr}>
        <TextField
          name="name"
          value={site?.name}
          onChange={handleChange}
          error={Boolean(error.name)}
          helperText={error.name}
          variant="standard"
          label="Website page title prefix"
          fullWidth
        />
      </Grid>
      <Grid item xs={6} paddingTop={3}>
        <Typography variant="caption" color={error.key ? 'error' : 'textSecondary'} className={classes.floatLabel}>
          Site
          location
        </Typography>
        <div className={classes.textFieldWrapper2}>
          {isNew ? (
            <>
              <Typography>
                https://
              </Typography>
              <Typography variant="body1" component="span">
                <AutosizeInput
                  value={site.key}
                  error={Boolean(error.key)}
                  onChange={onKeyChange}
                />
              </Typography>
              <Typography>.oncourse.cc</Typography>
            </>
          ) : (
            <Typography variant="body1">
              https://
              {site.key}
              .oncourse.cc
            </Typography>
          )}
        </div>
        {Boolean(error.key) && (<Typography className={classes.errorMessage}>{error.key}</Typography>)}
      </Grid>
      <Grid item xs={6} className={classes.pr} marginTop="auto">
        {isCustomDomainsAllow() ?
          (
            <Autocomplete
              size="small"
              options={[]}
              value={Object.keys(site.domains)}
              classes={{
                root: classes.domainsRoot,
                inputRoot: classes.domainsInput,
                hasClearIcon: classes.hasClearIcon
              }}
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  error={Boolean(error.domains)}
                  InputProps={{
                    ...params.InputProps,
                    margin: 'none',
                    endAdornment: params.inputProps.value
                      ? (
                        <Chip
                          size="small"
                          label="Add"
                          clickable
                          onClick={() => onAddDomain(params)}
                        />
                      )
                      : null
                  }}
                  helperText={Array.isArray(error.domains) ? error.domains.find((d) => d) : error.domains}
                  variant="standard"
                  label="Site domains"
                  margin="normal"
                  multiline
                />
              )}
              onChange={onDomainsChange}
              filterSelectedOptions
              multiple
              freeSolo
            />
          )
          : (
            <Alert severity="info" className="mt-2">
              <Link to="/"><MuiLink>Upgrade</MuiLink></Link>
              {' '}
              to the premium plan in order to add a custom domain
            </Alert>
          )}

      </Grid>
      <Grid item xs={6} marginTop="auto">
        <Collapse in={Boolean(domainItems.length)}>
          <TextField
            select
            fullWidth
            margin="normal"
            label="Primary hostname"
            value={site.primaryDomain || ''}
            onChange={onPrimaryDomainChange}
            helperText={error.primaryDomain}
            error={Boolean(error.primaryDomain)}
            name="primaryDomain"
            variant="standard"
          >
            {domainItems}
          </TextField>
        </Collapse>
      </Grid>
      <Grid item xs={12}>
        {domainWarnings}
        {domainWarnings.find((warnings) => warnings != null) && (
          <Button
            onClick={onRecheck}
            disableElevation
            color="success"
            variant="contained"
            className="mr-2"
          >
            Recheck
          </Button>
        )}
      </Grid>
      {isNew && (
        <Grid item xs={6} className={classes.pr}>
          <TemplateField
            label="Site template"
            name="webSiteTemplate"
            onChange={(val) => setFieldValue('webSiteTemplate', val)}
            value={site.webSiteTemplate}
            helperText={error.webSiteTemplate}
            error={Boolean(error.webSiteTemplate)}
            variant="standard"
            margin="normal"
            fullWidth
          />
        </Grid>
      )}
      <Grid item xs={12} className="mt-3">
        <TagInputList
          className={classes.tagsInput}
          label="Only show courses under the following tags"
          placeholder="Show all courses"
          tags={tags}
          input={{
            value: site.rootTags,
            onChange: (val) => setFieldValue('rootTags', val)
          }}
          meta={{
            error: error.rootTags,
            invalid: Boolean(error.rootTags)
          }}
          InputProps={{
            endAdornment: stubComponent(),
          }}
          allowParentSelect
        />
      </Grid>
    </Grid>
  );
};

export default URLs;